import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc } from '@goaltree/backend/convex/_generated/dataModel'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  AlertTriangle,
  BarChart3,
  Bell,
  CalendarClock,
  CheckCircle2,
  Flag,
  Flame,
  Heart,
  Lightbulb,
  Mail,
  MessageSquareText,
  PencilLine,
  RefreshCw,
  Sparkles,
  Sprout,
  Sun,
  Target,
  Trophy,
  UserMinus,
  UserPlus,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { isFromCoach } from '@/components/coaching/coach-utils'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { PushToggleCard } from '@/components/push-toggle-card'
import { SiteHeader } from '@/components/site-header'
import { isPersonalOnly } from '@/lib/coaching-mode'
import { optimisticMarkAllRead, optimisticMarkRead } from '@/lib/notifs-optimistic'
import { useUserMode } from '@/store/user-mode.store'

export const Route = createFileRoute('/_authenticated/notifications')({
  head: () => ({
    meta: [{ title: 'Notifications — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

type Notification = Doc<'notificationHistory'>
type Filter = 'all' | 'goals' | 'reminders' | 'social'
type ActionKey =
  | 'actionViewPlan'
  | 'actionOpenToday'
  | 'actionViewProfile'
  | 'actionViewGoal'
  | 'actionViewTask'
  | 'actionOpen'

const TINT = {
  blue: 'bg-blue-500/13 text-blue-500',
  green: 'bg-green-500/13 text-green-500',
  amber: 'bg-amber-500/13 text-amber-500',
  teal: 'bg-teal-500/13 text-teal-500',
  red: 'bg-destructive/13 text-destructive',
  accent: 'bg-primary/13 text-primary-text',
}

const TYPE_META: Record<
  string,
  { Icon: typeof Bell; tint: string; group: Exclude<Filter, 'all'>; action: ActionKey }
> = {
  replan: { Icon: RefreshCw, tint: TINT.blue, group: 'goals', action: 'actionViewPlan' },
  goalRolled: { Icon: RefreshCw, tint: TINT.blue, group: 'goals', action: 'actionViewPlan' },
  goalMissed: { Icon: AlertTriangle, tint: TINT.red, group: 'goals', action: 'actionViewGoal' },
  goalFallingBehind: {
    Icon: BarChart3,
    tint: TINT.amber,
    group: 'goals',
    action: 'actionViewGoal',
  },
  milestoneCompleted: { Icon: Flag, tint: TINT.teal, group: 'goals', action: 'actionViewGoal' },
  goalCompleted: { Icon: Trophy, tint: TINT.teal, group: 'goals', action: 'actionViewGoal' },
  coachEdit: { Icon: PencilLine, tint: TINT.accent, group: 'goals', action: 'actionViewGoal' },
  coachNote: { Icon: MessageSquareText, tint: TINT.green, group: 'social', action: 'actionOpen' },
  coachReply: { Icon: MessageSquareText, tint: TINT.green, group: 'social', action: 'actionOpen' },
  coachLinked: { Icon: UserPlus, tint: TINT.green, group: 'social', action: 'actionOpen' },
  coachEnded: { Icon: UserMinus, tint: TINT.amber, group: 'social', action: 'actionOpen' },
  firstGoalNudge: { Icon: Target, tint: TINT.accent, group: 'goals', action: 'actionOpen' },
  newFollower: { Icon: UserPlus, tint: TINT.green, group: 'social', action: 'actionViewProfile' },
  groveJoinRequest: { Icon: UserPlus, tint: TINT.green, group: 'social', action: 'actionOpen' },
  groveJoinApproved: { Icon: Sprout, tint: TINT.green, group: 'social', action: 'actionOpen' },
  dayCheers: { Icon: Heart, tint: TINT.accent, group: 'social', action: 'actionOpen' },
  momentumBoost: { Icon: Sprout, tint: TINT.green, group: 'social', action: 'actionOpen' },
  storyReaction: { Icon: Heart, tint: TINT.accent, group: 'social', action: 'actionOpen' },
  storyReply: {
    Icon: MessageSquareText,
    tint: TINT.green,
    group: 'social',
    action: 'actionOpen',
  },
  adminSignup: { Icon: UserPlus, tint: TINT.green, group: 'social', action: 'actionViewProfile' },
  adminSubscription: { Icon: Sparkles, tint: TINT.accent, group: 'social', action: 'actionOpen' },
  adminNewsletter: { Icon: Mail, tint: TINT.accent, group: 'social', action: 'actionOpen' },
  streakAtRisk: { Icon: Flame, tint: TINT.amber, group: 'reminders', action: 'actionOpenToday' },
  celebration: {
    Icon: CheckCircle2,
    tint: TINT.teal,
    group: 'reminders',
    action: 'actionOpenToday',
  },
  dailySurface: { Icon: Sun, tint: TINT.accent, group: 'reminders', action: 'actionOpenToday' },
  habitSuggestion: { Icon: Lightbulb, tint: TINT.accent, group: 'reminders', action: 'actionOpen' },
  weMissYou: { Icon: Bell, tint: TINT.accent, group: 'reminders', action: 'actionOpenToday' },
  taskDue: { Icon: CalendarClock, tint: TINT.accent, group: 'reminders', action: 'actionViewTask' },
  taskDue1hr: {
    Icon: CalendarClock,
    tint: TINT.accent,
    group: 'reminders',
    action: 'actionViewTask',
  },
}
const FALLBACK_META = {
  Icon: Bell,
  tint: TINT.accent,
  group: 'reminders' as const,
  action: 'actionOpen' as const,
}

// Same-type repeats that carry no unique info; per-task/per-goal types stay separate.
const ROUTINE_TYPES = new Set([
  'dailySurface',
  'weMissYou',
  'streakAtRisk',
  'celebration',
  'firstGoalNudge',
])

const FILTERS: Filter[] = ['all', 'goals', 'reminders', 'social']
const FILTER_LABEL = {
  all: 'notifications.filterAll',
  goals: 'notifications.filterGoals',
  reminders: 'notifications.filterReminders',
  social: 'notifications.filterSocial',
} as const
const EMPTY_COPY = {
  all: ['notifications.empty', 'notifications.emptyDesc'],
  goals: ['notifications.emptyGoals', 'notifications.emptyGoalsDesc'],
  reminders: ['notifications.emptyReminders', 'notifications.emptyRemindersDesc'],
  social: ['notifications.emptySocial', 'notifications.emptySocialDesc'],
} as const

type DayGroup = 'today' | 'yesterday' | 'thisWeek' | 'earlier'
const DAY_GROUP_LABEL = {
  today: 'notifications.groupToday',
  yesterday: 'notifications.groupYesterday',
  thisWeek: 'notifications.groupThisWeek',
  earlier: 'notifications.groupEarlier',
} as const

const LEADING_EMOJI = /^(?:\p{Extended_Pictographic}|\u2713|\uFE0F|\u200D|\s)+/u
const TRAILING_EMOJI = /(?:\p{Extended_Pictographic}|\uFE0F|\u200D|\s)+$/u
function stripEmoji(s: string) {
  return s.replace(LEADING_EMOJI, '').replace(TRAILING_EMOJI, '') || s
}

// Bodies may embed a task description after "///" — drop it inside the quoted
// name, show it as a dash-joined tail otherwise.
function cleanBody(body: string) {
  return body.replace(/\s*\/\/\/[^"]*"/, '"').replace(/\s*\/\/\/\s*/, ' — ')
}

const DAY_MS = 86_400_000
function startOfDay(ts: number) {
  const d = new Date(ts)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function dayGroup(ts: number, todayStart: number): DayGroup {
  if (ts >= todayStart) return 'today'
  if (ts >= todayStart - DAY_MS) return 'yesterday'
  if (ts >= todayStart - 6 * DAY_MS) return 'thisWeek'
  return 'earlier'
}

function useRelativeTime() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { weekdayTime, monthDay, monthDayYear } = useMemo(
    () => ({
      weekdayTime: new Intl.DateTimeFormat(lang, {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
      monthDay: new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }),
      monthDayYear: new Intl.DateTimeFormat(lang, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    }),
    [lang],
  )
  return (ts: number, todayStart: number) => {
    const mins = Math.floor((Date.now() - ts) / 60_000)
    if (mins < 1) return t('notifications.justNow')
    if (mins < 60) return t('notifications.minutesAgo', { count: mins })
    const group = dayGroup(ts, todayStart)
    if (group === 'today') return t('notifications.hoursAgo', { count: Math.floor(mins / 60) })
    if (group !== 'earlier') return weekdayTime.format(ts)
    return new Date(ts).getFullYear() === new Date().getFullYear()
      ? monthDay.format(ts)
      : monthDayYear.format(ts)
  }
}

type Entry = { head: Notification; older: Notification[] }

function collapseRoutine(rows: Notification[]): Entry[] {
  const entries: Entry[] = []
  const byType = new Map<string, Entry>()
  for (const n of rows) {
    const existing = ROUTINE_TYPES.has(n.type) ? byType.get(n.type) : undefined
    if (existing) {
      existing.older.push(n)
      continue
    }
    const entry = { head: n, older: [] }
    entries.push(entry)
    if (ROUTINE_TYPES.has(n.type)) byType.set(n.type, entry)
  }
  return entries
}

// Cache across remounts so the list doesn't flash the skeleton on every screen focus.
let lastKnownNotifications: Notification[] | null = null

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const liveNotifications = useQuery(api.notifications.list)
  const markAllRead = useMutation(api.notifications.markAllRead).withOptimisticUpdate(
    optimisticMarkAllRead,
  )
  const markRead = useMutation(api.notifications.markRead).withOptimisticUpdate(optimisticMarkRead)
  const [filter, setFilter] = useState<Filter>('all')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    if (liveNotifications !== undefined) lastKnownNotifications = liveNotifications
  }, [liveNotifications])
  const notifications = liveNotifications ?? lastKnownNotifications ?? undefined

  // Parse the stored url into the router's path + search so deep links like
  // ?focusTask=… scroll to / highlight the task.
  function open(entry: Notification[]) {
    for (const n of entry) if (n.readAt === null) void markRead({ id: n._id })
    const url = entry[0].url
    if (!url) return
    const u = new URL(url, window.location.origin)
    // Personal notifications (the AI coach, Today) open on the personal side.
    if (isPersonalOnly(u.pathname)) useUserMode.getState().setMode('personal')
    const search = Object.fromEntries(u.searchParams.entries())
    const hash = u.hash.slice(1) || undefined
    navigate({ to: u.pathname, search, hash } as unknown as Parameters<typeof navigate>[0])
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const todayStart = startOfDay(Date.now())
  const filtered = (notifications ?? []).filter(
    (n) => filter === 'all' || (TYPE_META[n.type] ?? FALLBACK_META).group === filter,
  )
  const sections: { group: DayGroup; entries: Entry[] }[] = []
  for (const entry of collapseRoutine(filtered)) {
    const group = dayGroup(entry.head._creationTime, todayStart)
    const last = sections[sections.length - 1]
    if (last?.group === group) last.entries.push(entry)
    else sections.push({ group, entries: [entry] })
  }
  const hasUnread = notifications?.some((n) => n.readAt === null) ?? false

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('nav.notifications')} showInbox={false} />
        <SiteHeader className="hidden lg:flex" />
        <div className="flex-1 overflow-y-auto pb-36 lg:pb-24">
          <div className="bg-background px-4 pt-4 pb-4">
            <div className="mx-auto max-w-(--page-col)">
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    {t('nav.notifications')}
                  </p>
                  <h1 className="text-3xl font-black text-foreground">
                    {t('notifications.title')}
                  </h1>
                </div>
                {hasUnread && (
                  <button
                    type="button"
                    onClick={() => markAllRead()}
                    className="mb-1 shrink-0 cursor-pointer text-xs font-semibold text-primary-text hover:underline"
                  >
                    {t('notifications.markAllRead')}
                  </button>
                )}
              </div>

              <PushToggleCard className="mb-4" />

              <div className="mb-4 flex flex-wrap gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={cn(
                      'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      filter === f
                        ? 'border-primary/40 bg-primary/10 text-primary-text'
                        : 'border-border/60 text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                    )}
                  >
                    {t(FILTER_LABEL[f])}
                  </button>
                ))}
              </div>

              {!notifications ? (
                <section className="mb-4">
                  <div className="px-1 pt-2 pb-1">
                    <div className="h-2.5 w-14 animate-pulse rounded bg-muted/50" />
                  </div>
                  {[66, 52, 74, 58, 48].map((w) => (
                    <div key={w} className="flex gap-3 border-b border-border/60 px-1 py-3">
                      <div className="mt-0.5 h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted/50" />
                      <div className="min-w-0 flex-1">
                        <div
                          className="h-3.5 animate-pulse rounded bg-muted/50"
                          style={{ width: `${w}%` }}
                        />
                        <div className="mt-1.5 h-3 w-full animate-pulse rounded bg-muted/30" />
                        <div className="mt-2 h-2.5 w-24 animate-pulse rounded bg-muted/30" />
                      </div>
                    </div>
                  ))}
                </section>
              ) : sections.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/40">
                    <Bell className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm font-medium text-foreground/60">
                    {t(EMPTY_COPY[filter][0])}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground/60">
                    {t(EMPTY_COPY[filter][1])}
                  </p>
                </div>
              ) : (
                sections.map(({ group, entries }) => (
                  <section key={group} className="mb-4">
                    <h2 className="px-1 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                      {t(DAY_GROUP_LABEL[group])}
                    </h2>
                    {entries.map((entry) => {
                      const isExpanded = expanded.has(entry.head._id)
                      return (
                        <div key={entry.head._id}>
                          <NotificationRow
                            n={entry.head}
                            todayStart={todayStart}
                            unread={[entry.head, ...entry.older].some((n) => n.readAt === null)}
                            repeatCount={entry.older.length + 1}
                            expanded={isExpanded}
                            onToggleRepeats={() => toggleExpanded(entry.head._id)}
                            onOpen={() => open([entry.head, ...entry.older])}
                          />
                          {isExpanded &&
                            entry.older.map((n) => (
                              <NotificationRow
                                key={n._id}
                                n={n}
                                todayStart={todayStart}
                                unread={n.readAt === null}
                                nested
                                onOpen={() => open([n])}
                              />
                            ))}
                        </div>
                      )
                    })}
                  </section>
                ))
              )}

              {notifications && (
                <div className="py-6 text-center">
                  <Link
                    to="/settings"
                    hash="notifications"
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
                  >
                    {t('notifications.settingsLink')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

function NotificationRow({
  n,
  todayStart,
  unread,
  repeatCount = 1,
  expanded = false,
  nested = false,
  onToggleRepeats,
  onOpen,
}: {
  n: Notification
  todayStart: number
  unread: boolean
  repeatCount?: number
  expanded?: boolean
  nested?: boolean
  onToggleRepeats?: () => void
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const relativeTime = useRelativeTime()
  const meta = TYPE_META[n.type] ?? FALLBACK_META
  const { Icon } = meta
  const action =
    meta.action === 'actionOpenToday' && !n.url?.startsWith('/today') ? 'actionOpen' : meta.action

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className={cn(
        'flex cursor-pointer gap-3 border-b border-border/60 px-1 py-3 transition-colors hover:bg-muted/40',
        nested && 'pl-12',
      )}
    >
      <div
        className={cn(
          'relative mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          meta.tint,
        )}
      >
        <Icon className="h-4 w-4" />
        {isFromCoach(n) && (
          <span className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full bg-coach ring-2 ring-background" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
          <p
            className={cn(
              'min-w-0 truncate text-sm leading-snug text-foreground',
              unread ? 'font-semibold' : 'font-normal',
            )}
          >
            {stripEmoji(n.title)}
          </p>
          {repeatCount > 1 && (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={t('notifications.showOlder', { count: repeatCount - 1 })}
              onClick={(e) => {
                e.stopPropagation()
                onToggleRepeats?.()
              }}
              className={cn(
                'shrink-0 cursor-pointer rounded-full px-1.5 py-px font-mono text-[10px] font-semibold transition-colors',
                expanded
                  ? 'bg-primary/15 text-primary-text'
                  : 'bg-muted text-muted-foreground hover:text-foreground',
              )}
            >
              {t('notifications.repeatCount', { count: repeatCount })}
            </button>
          )}
        </div>
        {n.body && (
          <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-muted-foreground">
            {cleanBody(n.body)}
          </p>
        )}
        <div className="mt-1 flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
          <span>{relativeTime(n._creationTime, todayStart)}</span>
          {n.url && (
            <>
              <span aria-hidden>·</span>
              <span className="text-primary-text">{t(`notifications.${action}`)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
